import { collection, doc, getDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { callable } from '../callable';

// ─── Socratic dialogue (Delivery 3b) ─────────────────────────────────────────
// Callables for the stateful dialogue + the teacher config, plus direct Firestore
// reads for the student's OWN sessions and the assignment's socratic config (the
// rules allow both). Vocabulary rule: the UI never says "IA".

export type SocraticLevel = 'review' | 'probe' | 'defense';
export type SocraticCoverage = 'covered' | 'partial' | 'untouched';
export type SocraticSessionStatus = 'in_progress' | 'closed' | 'evaluated' | 'expired';

export interface SocraticTurnDto {
  index: number;
  question: string;
  answer: string | null;
  askedAt: string;
  answeredAt: string | null;
}

export interface AxisCoverageResultDto {
  axisId: string;
  coverage: SocraticCoverage;
  /** Neutral scaffold label as an i18n key (resolved with t()); may be absent on
   *  sessions evaluated before this was added. */
  label?: string;
}

export interface SocraticEvaluationDto {
  prose: string;
  coverageMap: AxisCoverageResultDto[];
}

/** Coaching feedback for a PRACTICE session (dialogue-skill tips, not a grade). */
export interface SocraticCoachingDto {
  prose: string;
  suggestions: string[];
}

export type SocraticMode = 'evaluated' | 'practice';

// ── Growth trajectory (3c-4 F2) ──
export type SocraticGrowthDimensionKey = 'topic_mastery' | 'grounding' | 'writing' | 'depth';
export type GrowthTrend = 'insufficient' | 'needs_work' | 'steady' | 'growing';

export interface DimensionTrajectoryDto {
  key: SocraticGrowthDimensionKey;
  currentLevel: number | null;
  recentAvg: number | null;
  priorAvg: number | null;
  trend: GrowthTrend;
  latestEvidence?: { quote: string; rationale: string; level: number };
}

export type TopicMasteryStatus = 'in_progress' | 'consolidating' | 'mastered';

export interface TopicMasteryDto {
  standardId: string;
  level: number;
  status: TopicMasteryStatus;
  sessions: number;
  assessedAt: string;
}

export interface PracticeTrajectoryDto {
  coldStart: boolean;
  assessedCount: number;
  dimensions: DimensionTrajectoryDto[];
  masteryByTopic: TopicMasteryDto[];
  weakestDimension: SocraticGrowthDimensionKey | null;
}

// ── Teacher view + calibration (3c-4 F4) ──
export interface PracticeStudentSummaryDto {
  studentUid: string;
  studentPersonId?: string;
  /** Lo resuelve el servidor (PR 3A). Ausente si no hay ficha legible. */
  studentName?: string;
  assessed: number;
  lastAssessedAt?: string;
}

export interface CalibrationSummaryDto {
  overridden: number;
  agreements: number;
  agreementRate: number | null;
}

export interface GrowthDimensionScoreDto {
  key: SocraticGrowthDimensionKey;
  level: number;
  evidenceQuote: string;
  rationale: string;
  teacherOverride?: number;
}

export interface StudentPracticeAssessmentDto {
  sessionId: string;
  standardId: string;
  assessedAt: string;
  dimensions: GrowthDimensionScoreDto[];
}

export interface SocraticSessionDto {
  id: string;
  tenantId: string;
  mode: SocraticMode;
  /** Absent for practice sessions. */
  assignmentId?: string;
  studentUid: string;
  /** Directory personId (name resolution via /people). Absent on legacy/admin sessions. */
  studentPersonId?: string;
  standardId: string;
  level: SocraticLevel;
  attemptNumber: number;
  locale: 'es' | 'en';
  status: SocraticSessionStatus;
  turns: SocraticTurnDto[];
  maxReQuestions: number;
  evaluation?: SocraticEvaluationDto;
  /** Coaching feedback (practice sessions). */
  coaching?: SocraticCoachingDto;
  /** Staff formative note (socratic has no numeric grade). Absent until written. */
  teacherNote?: string;
  /** When staff last marked this session reviewed (roster workflow marker). */
  reviewedAt?: string;
}

export interface SocraticAssignmentConfigDto {
  rootQuestion: string;
  standardId: string;
  level: SocraticLevel;
  maxAttempts: number;
}

export const socraticApi = {
  start(data: { tenantId: string; assignmentId: string; locale: 'es' | 'en' }): Promise<{
    session: SocraticSessionDto;
  }> {
    const fn = callable<typeof data, { session: SocraticSessionDto }>('startSocraticSession');
    return fn(data).then((r) => r.data);
  },

  advance(data: { tenantId: string; sessionId: string; answer: string }): Promise<{
    session: SocraticSessionDto;
    closed: boolean;
  }> {
    const fn = callable<typeof data, { session: SocraticSessionDto; closed: boolean }>(
      'advanceSocraticTurn',
    );
    return fn(data).then((r) => r.data);
  },

  close(data: { tenantId: string; sessionId: string }): Promise<{ session: SocraticSessionDto }> {
    const fn = callable<typeof data, { session: SocraticSessionDto }>('closeSocraticSession');
    return fn(data).then((r) => r.data);
  },

  evaluate(data: { tenantId: string; sessionId: string }): Promise<{
    session: SocraticSessionDto;
    evaluation: SocraticEvaluationDto;
  }> {
    const fn = callable<
      typeof data,
      { session: SocraticSessionDto; evaluation: SocraticEvaluationDto }
    >('evaluateSocraticSession');
    return fn(data).then((r) => r.data);
  },

  // ── Practice (Frente B): self-study, quota-limited, coaching at close ──

  /** Start a practice dialogue against an approved standard (server picks a root
   *  question from its bank + enforces the daily quota). Advance/close reuse the
   *  same callables as the evaluated flow. */
  startPractice(data: {
    tenantId: string;
    standardId: string;
    questionIndex?: number;
    locale?: 'es' | 'en';
    level?: SocraticLevel;
    audienceLevel?: 'lay' | 'masters' | 'doctorate';
  }): Promise<{ session: SocraticSessionDto; remainingToday: number }> {
    const fn = callable<typeof data, { session: SocraticSessionDto; remainingToday: number }>(
      'startPracticeSession',
    );
    return fn(data).then((r) => r.data);
  },

  /** The student's self-referenced growth trajectory across their practice. */
  getTrajectory(data: { tenantId: string }): Promise<{ trajectory: PracticeTrajectoryDto }> {
    const fn = callable<typeof data, { trajectory: PracticeTrajectoryDto }>(
      'getPracticeTrajectory',
    );
    return fn(data).then((r) => r.data);
  },

  // ── Teacher view + calibration (F4, staff) ──

  /** Staff: students who have assessed practices. */
  listPracticeStudents(data: {
    tenantId: string;
  }): Promise<{ students: PracticeStudentSummaryDto[] }> {
    const fn = callable<typeof data, { students: PracticeStudentSummaryDto[] }>(
      'listPracticeStudents',
    );
    return fn(data).then((r) => r.data);
  },

  /** Staff: one student's trajectory + calibration + per-session assessments. */
  getStudentGrowth(data: { tenantId: string; studentUid: string }): Promise<{
    trajectory: PracticeTrajectoryDto;
    calibration: CalibrationSummaryDto;
    assessments: StudentPracticeAssessmentDto[];
  }> {
    const fn = callable<
      typeof data,
      {
        trajectory: PracticeTrajectoryDto;
        calibration: CalibrationSummaryDto;
        assessments: StudentPracticeAssessmentDto[];
      }
    >('getStudentGrowth');
    return fn(data).then((r) => r.data);
  },

  /** Staff: set (level 1–4) or clear (null) a teacher override on a dimension. */
  saveGrowthOverride(data: {
    tenantId: string;
    sessionId: string;
    dimensionKey: SocraticGrowthDimensionKey;
    level: number | null;
  }): Promise<{ session: SocraticSessionDto }> {
    const fn = callable<typeof data, { session: SocraticSessionDto }>('saveGrowthOverride');
    return fn(data).then((r) => r.data);
  },

  /** Generate coaching for a CLOSED practice session (dialogue-skill feedback). */
  coachPractice(data: { tenantId: string; sessionId: string }): Promise<{
    session: SocraticSessionDto;
    coaching: SocraticCoachingDto;
  }> {
    const fn = callable<
      typeof data,
      { session: SocraticSessionDto; coaching: SocraticCoachingDto }
    >('coachPracticeSession');
    return fn(data).then((r) => r.data);
  },

  /** Staff: save (or clear, with an empty string) the teacher's formative note on
   *  a closed/evaluated dialogue. Marks the session reviewed. */
  saveTeacherNote(data: { tenantId: string; sessionId: string; note: string }): Promise<{
    session: SocraticSessionDto;
  }> {
    const fn = callable<typeof data, { session: SocraticSessionDto }>('saveSocraticTeacherNote');
    return fn(data).then((r) => r.data);
  },

  saveConfig(data: {
    tenantId: string;
    sectionId: string;
    assignmentId?: string;
    name: string;
    weight?: number;
    rootQuestion: string;
    standardId: string;
    level: SocraticLevel;
    maxAttempts: number;
    dueAt?: string;
    lockAt?: string;
  }): Promise<{ id: string }> {
    const fn = callable<typeof data, { id: string }>('saveSocraticConfig');
    return fn(data).then((r) => r.data);
  },

  // ── Direct Firestore reads (rules-permitted) ──

  /** The student's own sessions for an assignment — to resume an in_progress one
   *  and to know which attempt they're on. */
  async listMySessions(
    tenantId: string,
    assignmentId: string,
    studentUid: string,
  ): Promise<SocraticSessionDto[]> {
    const snap = await getDocs(
      query(
        collection(db, `tenants/${tenantId}/socraticSessions`),
        where('assignmentId', '==', assignmentId),
        where('studentUid', '==', studentUid),
      ),
    );
    return snap.docs.map((docSnap) => docSnap.data() as SocraticSessionDto);
  },

  /** Staff: closed/evaluated sessions for an assignment (decision 11 — no live
   *  view), with the student's name. The SERVER assembles it (PR 3A): it checks
   *  the caller teaches the assignment's section, instead of the rules letting
   *  any teacher read every session and every person record. */
  async listSessionsForAssignment(
    tenantId: string,
    assignmentId: string,
  ): Promise<Array<SocraticSessionDto & { studentName: string | null }>> {
    const fn = callable<
      { tenantId: string; assignmentId: string },
      { sessions: Array<SocraticSessionDto & { studentName: string | null }> }
    >('listAssignmentSocraticSessions');
    return (await fn({ tenantId, assignmentId })).data.sessions;
  },

  /** Read the assignment's socratic config (root question, level, maxAttempts). */
  async getConfig(
    tenantId: string,
    assignmentId: string,
  ): Promise<SocraticAssignmentConfigDto | null> {
    const snap = await getDoc(doc(db, `tenants/${tenantId}/assignments/${assignmentId}`));
    const data = snap.data();
    return (data?.['socraticConfig'] as SocraticAssignmentConfigDto | undefined) ?? null;
  },
};

/** Extract the machine-readable reason from an HttpsError (client branching). */
export function socraticReason(err: unknown): string | undefined {
  return (err as { details?: { reason?: string } })?.details?.reason;
}
